<template>
    <UIDialog v-if="modals.reveal_emails.is_open" :open="modals.reveal_emails.is_open" @update:open="closeModal">
        <DialogScrollContent :class="'max-w-xl border-none p-0'">
            <DialogHeader class="px-6 pt-8">
                <DialogTitle>Bulk Email Reveal</DialogTitle>
            </DialogHeader>

            <div v-if="loading" class="mx-6 rounded border border-slate-200 bg-slate-100 px-4 py-2 mb-4 text-sm">
                <div class="flex">
                    <AppIcon name="spinner-third" class="mr-2 h-4 w-4 animate-spin" />
                    <span>Checking...</span>
                </div>
            </div>
            <template v-else-if="unrevealedEmails > remainingQuota && !isLegacyBilling">
                <div class="border rounded border-rose-200 bg-rose-100 px-4 py-2 mx-6 text-sm mb-6">
                    <p>You currently only have <span class="font-semibold">{{ $filters.formatWholeNumber(remainingQuota) }} email reveal</span> credits available, revealing these emails would require <span class="font-semibold">{{ unrevealedEmails }} email reveal</span> credits.</p>
                </div>
            </template>
            <template v-else-if="unrevealedEmails === 0 && !isLegacyBilling">
                <div class="border rounded border-slate-200 bg-slate-100 px-4 py-2 mx-6 text-sm mb-6">
                    <p>There are no emails to reveal for the selected creators.</p>
                </div>
            </template>
            <template v-else>
                <div v-if="!isLegacyBilling" class="border border-slate-200 bg-slate-100 rounded px-4 py-2 mx-6 text-sm">
                    <p>You will use <span class="font-semibold">{{ $filters.formatWholeNumber(unrevealedEmails) }} email reveal</span> credits to reveal these emails out of your plans <span class="font-semibold">{{ $filters.formatWholeNumber(remainingQuota) }}</span> remaining <span class="font-semibold">email reveal</span> credits.</p>
                    <p v-if="excludedEmails > 0" class="mt-2 text-sm">{{ $filters.formatWholeNumber(excludedEmails) }} creator(s) were excluded (emails already revealed or not found).</p>
                </div>
                <div class="px-6 py-6">
                    <button
                        class="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 flex justify-center disabled:opacity-50"
                        :disabled="isRevealingBulk || unrevealedEmails === 0"
                        @click="revealEmails"
                    >
                        <AppIcon v-if="isRevealingBulk" name="spinner-third" class="animate-spin w-4 h-4 mr-2 my-auto" />
                        Reveal {{ unrevealedEmails }} Emails
                    </button>
                </div>

            </template>
        </DialogScrollContent>
    </UIDialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { Dialog as UIDialog, DialogHeader, DialogTitle, DialogScrollContent } from '@/components/ui/dialog'
import _ from 'lodash'

const store = useStore()
const loading = ref(true)
const unrevealedEmails = ref(0)
const excludedEmails = ref(0)
const creatorsToReveal = ref([])

const isWaitingForStatus = ref(true)
const remainingQuota = ref(0)

const isLegacyBilling = computed(() => store.getters.isLegacyBilling)
const modals = computed(() => store.getters.modals)
const isRevealingBulk = computed(() => store.getters.isRevealingBulk)

watch(() => modals.value.reveal_emails.is_open, async (isOpen) => {

    creatorsToReveal.value = []
    unrevealedEmails.value = 0
    excludedEmails.value = 0

    if (isOpen && modals.value.reveal_emails.creators?.length) {
        loading.value = true
        isWaitingForStatus.value = true

        try {
            const creators = modals.value.reveal_emails.creators
            creators.map(creator =>
                store.dispatch('fetchRevealStatus', {
                    quotaType : 'profile-unlock',
                    keyIds : ['superview', creator.platform_id, creator.platform_user_id]
                })
            )
            checkRevealState()
        } catch (error) {
            console.error('Error checking email statuses:', error)
            loading.value = false
        }

    }
    else {
        isWaitingForStatus.value = false
    }
}, { immediate: true })


watch(() => store.getters.unlocks, async () => {
    if (isWaitingForStatus.value) {
        checkRevealState()
    }
    else {
        loading.value = false
    }

}, { immediate: true, deep: true })

const closeModal = () => {
    store.commit('modal:close', 'reveal_emails')
}

const revealEmails = async () => {
    try {
        let items = _.map(creatorsToReveal.value, creator => {
            return ['superview', creator.platform_id, creator.platform_user_id]
        })
        await store.dispatch('bulkReveal', { quotaType: 'profile-unlock', items })
        closeModal()
    } catch (error) {
        console.error('Error revealing emails:', error)
    }
}

const checkRevealState = () => {
    const creators = modals.value.reveal_emails.creators

        let allFoundInStore = true

        _.forEach(creators, creator => {
            const key = `profile-unlock:superview:${creator.platform_id}:${creator.platform_user_id}`
            const revealStatus = store.getters.unlocks[key]
            if (!revealStatus) {
                allFoundInStore = false
            }
        })

        if (allFoundInStore) {

            creatorsToReveal.value = []

            _.forEach(creators, creator => {
                const key = `profile-unlock:superview:${creator.platform_id}:${creator.platform_user_id}`
                const revealStatus = store.getters.unlocks[key]
                if (revealStatus?.is_found && !revealStatus?.is_previously_unlocked && revealStatus?.state !== 'revealed' && revealStatus?.is_revealable) {
                    console.log(revealStatus)
                    creatorsToReveal.value.push(creator)
                }
            })

            unrevealedEmails.value = creatorsToReveal.value.length
            excludedEmails.value = creators.length - creatorsToReveal.value.length

            loading.value = false
        }
}
</script>